import {
  trackerPickerDeviceId,
  trackerPickerDeviceImei,
  trackerPickerDeviceSerial,
} from '@gpx-spa/ui/src/utils/tracker-picker';
import { format, parseISO } from 'date-fns';
import { exceptionTypeTitle } from '@/utils/shipment-exception-catalog';
import {
  namedTrackerLabel,
  type NamedTracker,
} from '@/utils/named-tracker-set';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Same wall-clock pattern as the exception detail card (`MMM d, h:mm aa`). */
export function formatExceptionOccurredAt(
  occurredAt: string | null | undefined,
): string | null {
  if (!occurredAt) return null;
  const d = parseISO(occurredAt);
  if (Number.isNaN(d.getTime())) return null;
  return format(d, 'MMM d, h:mm aa');
}

function trackerRosterItemHtml(tracker: NamedTracker, isLast: boolean): string {
  const label = escapeHtml(namedTrackerLabel(tracker));
  const serial = escapeHtml(
    trackerPickerDeviceSerial({
      id: tracker.id,
      serial: tracker.serial ?? undefined,
    }),
  );
  const imei = escapeHtml(
    trackerPickerDeviceImei({
      id: tracker.id,
      imei: tracker.imei ?? undefined,
    }),
  );
  const idLabel = escapeHtml(trackerPickerDeviceId({ id: tracker.id }));
  const border = isLast ? '' : ' border-b border-gray-100';
  return `<li class="flex items-center gap-3${border} px-3 py-2">
      <div class="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100">
        <i class="pi pi-wifi text-sm text-gray-400" aria-hidden="true"></i>
      </div>
      <div class="min-w-0 flex-1">
        <div class="truncate text-sm font-medium text-gray-900">${label}</div>
        <div class="truncate font-mono text-xs text-gpx_gray-200">Serial: ${serial}<span class="mx-1.5 opacity-50" aria-hidden="true">·</span>IMEI: ${imei}<span class="mx-1.5 opacity-50" aria-hidden="true">·</span>ID: ${idLabel}</div>
      </div>
    </li>`;
}

function trackerRosterHtml(trackers: readonly NamedTracker[]): string {
  if (trackers.length === 0) return '';
  const items = trackers
    .map((tracker, i) => trackerRosterItemHtml(tracker, i === trackers.length - 1))
    .join('');
  return `<ul class="mt-1.5 w-[min(22rem,85vw)] overflow-hidden rounded-md">${items}</ul>`;
}

/** Map popup/tooltip: title, occurred time, then trackers (NamedTrackerRoster). */
export function exceptionMapPopupHtml(
  row: {
    type: string;
    occurred_at: string | null;
  },
  trackers: readonly NamedTracker[] = [],
): string {
  const title = escapeHtml(exceptionTypeTitle(row.type));
  const when = formatExceptionOccurredAt(row.occurred_at);
  const timeLine = when
    ? `<div class="gpx-shipment-map-tooltip__muted">${escapeHtml(when)}</div>`
    : '';
  return `<div class="gpx-shipment-map-tooltip"><div class="gpx-shipment-map-tooltip__title">${title}</div>${timeLine}${trackerRosterHtml(trackers)}</div>`;
}
